/**
 * API Service - Fetch wrapper with authentication
 * All API calls include X-API-Key header automatically
 */

import { getApiKey, clearApiKey } from './auth'
import { getCache, setCache, clearCache, clearCacheByPrefix, CacheKeys, CacheTTL } from './cache'

// API base URL from vite config
const API_URL = __API_URL__

/**
 * Custom API error class
 */
export class ApiError extends Error {
  constructor(status, message, data = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/tasks')
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise<Object>} Response data
 * @throws {ApiError} On API errors
 */
export async function api(endpoint, options = {}) {
  const apiKey = getApiKey()

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add auth header if we have an API key
  if (apiKey) {
    headers['X-API-Key'] = apiKey
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  // Handle 401 Unauthorized - clear auth and trigger redirect
  if (response.status === 401) {
    clearApiKey()
    // Dispatch custom event for auth context to handle
    window.dispatchEvent(new CustomEvent('auth:unauthorized'))
    throw new ApiError(401, 'Unauthorized - please log in again')
  }

  // Handle other error responses
  if (!response.ok) {
    let errorData = null
    try {
      errorData = await response.json()
    } catch {
      // Response wasn't JSON
    }
    throw new ApiError(
      response.status,
      errorData?.detail || `API error: ${response.status}`,
      errorData
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null
  }

  return response.json()
}

/**
 * GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 */
export async function get(endpoint, params = {}) {
  const queryString = new URLSearchParams(params).toString()
  const url = queryString ? `${endpoint}?${queryString}` : endpoint
  return api(url, { method: 'GET' })
}

/**
 * POST request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 */
export async function post(endpoint, body = {}) {
  return api(endpoint, { method: 'POST', body })
}

/**
 * PATCH request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 */
export async function patch(endpoint, body = {}) {
  return api(endpoint, { method: 'PATCH', body })
}

/**
 * DELETE request
 * @param {string} endpoint - API endpoint
 */
export async function del(endpoint) {
  return api(endpoint, { method: 'DELETE' })
}

/**
 * Validate API key by calling /auth/me
 * @returns {Promise<Object>} Partner data if valid
 * @throws {ApiError} If invalid
 */
export async function validateApiKey() {
  return get('/auth/me')
}

/**
 * GET with caching - checks cache first, fetches if expired
 * @param {string} endpoint - API endpoint
 * @param {string} cacheKey - Cache key to use
 * @param {number} ttl - Cache TTL in ms
 * @param {Object} params - Query parameters
 */
export async function getCached(endpoint, cacheKey, ttl = CacheTTL.MEDIUM, params = {}) {
  // Check cache first
  const cached = getCache(cacheKey)
  if (cached) return cached

  // Fetch from API
  const data = await get(endpoint, params)
  setCache(cacheKey, data, ttl)
  return data
}

/**
 * Prefetch common data on app load for faster navigation
 * Call this after authentication to warm the cache
 */
export async function prefetchCommonData() {
  // Fetch in parallel for speed
  await Promise.allSettled([
    getCached('/partners', CacheKeys.PARTNERS, CacheTTL.LONG),
    getCached('/session-tasks/definitions', CacheKeys.SESSION_TASK_DEFS, CacheTTL.MEDIUM),
  ])
}

// Export API_URL for debugging
export { API_URL }

// Re-export cache utilities for convenience
export { CacheKeys, CacheTTL, clearCache, clearCacheByPrefix } from './cache'
